import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import addDays from 'date-fns/addDays'
import format from 'date-fns/format'
import parse from 'date-fns/parse'
import parseISO from 'date-fns/parseISO'
import type Plotly from 'plotly.js'
import styled, { useTheme } from 'styled-components'
import { axisBase } from '~/packages/sentinel/visualization/utils/axis'
import Loader from '~/prix/react/components/loader'
import Plot from '~/prix/react/components/plot'
import { formatBR } from '~/packages/sentinel/visualization/utils/format'

const Wrapper = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  min-height: 0;

  svg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
    overflow: visible;
  }
`

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(1px);
  z-index: 1;
  pointer-events: none;
`

const PlotFrame = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  z-index: 1;
`

const ModernTooltipContainer = styled.div`
  position: fixed;
  min-width: 150px;
  background-color: white;
  padding: 12px;
  border-radius: 6px;
  box-shadow: 0 0px 15px rgba(0, 0, 0, 0.2);
  z-index: 99999;
  pointer-events: none;

  p {
    margin: 0;
  }
`

type LegacyProps = {
  values?: Array<number | null>
  highlights?: Array<boolean>
  predictions?: Array<boolean>
  isLoading?: boolean
  dates?: string[]
  disableTooltip?: boolean
  disableYAxis?: boolean
  highlightColor?: string
  selectedRange?: {
    startDate?: string | null
    endDate?: string | null
  }
}

type Series = {
  color: string
  values: Array<number | null>
  fillColor?: string
}

type BandsShaded = {
  from: number
  to: number
  color: string
  opacity?: number
}

type Bands = {
  upper?: number
  lower?: number
  median?: number
  shaded?: BandsShaded
}

type ModernProps = {
  series?: Series[]
  bands?: Bands
  disableTooltip?: boolean
  disableYAxis?: boolean
  disablePoints?: boolean
  padding?: number
}

type TimeSeriesPreviewProps = LegacyProps & ModernProps

interface ModernTooltipData {
  value: string
  seriesIndex: number
  pointIndex: number
}

interface MinMaxPoint {
  value: number
  index: number
}

function useObservedDimensions<T extends HTMLElement>() {
  const [element, setElement] = useState<T | null>(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  useEffect(() => {
    if (!element) {
      return
    }

    const updateDimensions = () => {
      const rect = element.getBoundingClientRect()
      const width = Math.max(0, Math.ceil(rect.width || element.clientWidth))
      const height = Math.max(0, Math.ceil(rect.height || element.clientHeight))

      setDimensions(previous =>
        previous.width === width && previous.height === height ? previous : { width, height },
      )
    }

    updateDimensions()

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        updateDimensions()
      })

      observer.observe(element)

      return () => {
        observer.disconnect()
      }
    }

    window.addEventListener('resize', updateDimensions)

    return () => {
      window.removeEventListener('resize', updateDimensions)
    }
  }, [element])

  return {
    dimensions,
    setObservedElement: setElement,
  }
}

function LegacyTimeSeriesPreview({
  values = [],
  highlights = [],
  predictions = [],
  isLoading = false,
  dates = [],
  disableTooltip = false,
  disableYAxis = false,
  highlightColor,
  selectedRange,
}: LegacyProps) {
  const theme = useTheme()
  const selectedRangeStart = selectedRange?.startDate ?? null
  const selectedRangeEnd = selectedRange?.endDate ?? null
  const normalizedDates = useMemo(() => dates.map(date => normalizeLegacyDate(date)), [dates])
  const normalizedValues = useMemo(
    () => values.map(value => (typeof value === 'number' && Number.isFinite(value) ? value : null)),
    [values],
  )
  const normalizedPredictions = useMemo(
    () => normalizedValues.map((_, index) => Boolean(predictions[index])),
    [normalizedValues, predictions],
  )
  const validValues = normalizedValues.filter((value): value is number => value !== null)
  const plotMargin = useMemo(
    () => ({
      l: disableYAxis ? 18 : 40,
      r: 4,
      t: 4,
      b: 24,
    }),
    [disableYAxis],
  )

  const pointColor = highlightColor ?? theme.colors.secondary
  const data = useMemo<Plotly.Data[]>(() => {
    const formatHoverText = (value: number | null, index: number, isPrediction: boolean) => {
      const label = normalizedDates[index]
        ? format(parseISO(normalizedDates[index]!), 'dd/MM/yyyy')
        : 'Sem data'
      const valueLabel = value === null ? 'Sem dados' : formatBR(value)

      return isPrediction
        ? `Data: ${label}<br>Valor previsto: ${valueLabel}`
        : `Data: ${label}<br>Valor: ${valueLabel}`
    }

    const hasPredictions = normalizedPredictions.some(Boolean)
    const actualValues = hasPredictions
      ? normalizedValues.map((value, index) => (normalizedPredictions[index] ? null : value))
      : normalizedValues
    const actualHoverText = normalizedValues.map((value, index) =>
      formatHoverText(value, index, false),
    )
    const highlightedIndexes = normalizedValues.reduce<number[]>((accumulator, _, index) => {
      if (highlights[index]) {
        accumulator.push(index)
      }

      return accumulator
    }, [])
    const traces: Plotly.Data[] = [
      {
        x: normalizedDates,
        y: actualValues,
        type: 'scatter',
        mode: 'lines',
        line: {
          color: theme.colors.primary,
          width: 2.5,
          shape: 'linear',
        },
        cliponaxis: false,
        hovertext: actualHoverText,
        hovertemplate: disableTooltip ? '<extra></extra>' : '%{hovertext}<extra></extra>',
        showlegend: false,
      },
    ]

    if (hasPredictions) {
      const predictedValues = normalizedValues.map((value, index) => {
        if (normalizedPredictions[index]) {
          return value
        }

        return normalizedPredictions[index + 1] ? value : null
      })

      traces.push({
        x: normalizedDates,
        y: predictedValues,
        type: 'scatter',
        mode: 'lines',
        line: {
          color: pointColor,
          width: 2.5,
          dash: 'dot',
          shape: 'linear',
        },
        cliponaxis: false,
        hovertext: normalizedValues.map((value, index) =>
          formatHoverText(value, index, normalizedPredictions[index]),
        ),
        hovertemplate: disableTooltip ? '<extra></extra>' : '%{hovertext}<extra></extra>',
        showlegend: false,
      })
    }

    if (highlightedIndexes.length > 0) {
      traces.push({
        x: highlightedIndexes.map(index => normalizedDates[index]),
        y: highlightedIndexes.map(index => normalizedValues[index]),
        type: 'scatter',
        mode: 'markers',
        marker: {
          size: 6,
          color: pointColor,
        },
        cliponaxis: false,
        hovertext: highlightedIndexes.map(index => formatHoverText(normalizedValues[index], index, false)),
        hovertemplate: disableTooltip ? '<extra></extra>' : '%{hovertext}<extra></extra>',
        showlegend: false,
      })
    }

    return traces
  }, [
    disableTooltip,
    highlights,
    normalizedDates,
    normalizedPredictions,
    normalizedValues,
    pointColor,
    theme.colors.primary,
  ])

  const layout = useMemo<Partial<Plotly.Layout>>(
    () => ({
      autosize: true,
      margin: plotMargin,
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      showlegend: false,
      hovermode: disableTooltip ? false : 'x unified',
      hoverlabel: {
        align: 'left',
        bgcolor: 'white',
        bordercolor: '#e5e7eb',
        font: {
          size: 12,
          color: '#111827',
          family: 'Figtree, sans-serif',
        },
      },
      xaxis: {
        ...axisBase,
        type: 'date',
        showgrid: false,
        tickformat: '%d/%m',
        hoverformat: '%d/%m/%Y',
        nticks: 7,
        title: '',
        tickfont: {
          size: 10,
          color: theme.colors.darkText,
          family: 'Figtree, sans-serif',
        },
      },
      yaxis: disableYAxis
        ? {
            visible: false,
          }
        : {
            ...axisBase,
            title: '',
            nticks: 4,
            tickformat: '~s',
            tickfont: {
              size: 10,
              color: theme.colors.darkText,
              family: 'Figtree, sans-serif',
            },
          },
    }),
    [disableTooltip, disableYAxis, plotMargin, theme.colors.darkText],
  )

  const selectionLayoutPatch = useMemo<Partial<Plotly.Layout>>(() => {
    if (!selectedRangeStart || !selectedRangeEnd || normalizedDates.length === 0) {
      return {
        shapes: [],
      }
    }

    const normalizedRangeStart = normalizeLegacyDate(selectedRangeStart)
    const normalizedRangeEnd = normalizeLegacyDate(selectedRangeEnd)
    const [rangeStart, rangeEnd] =
      normalizedRangeStart <= normalizedRangeEnd
        ? [normalizedRangeStart, normalizedRangeEnd]
        : [normalizedRangeEnd, normalizedRangeStart]
    const firstDate = normalizedDates[0]
    const lastDate = normalizedDates[normalizedDates.length - 1]
    const clampedStart =
      rangeStart < firstDate ? firstDate : rangeStart > lastDate ? lastDate : rangeStart
    const clampedEnd =
      rangeEnd < firstDate ? firstDate : rangeEnd > lastDate ? lastDate : rangeEnd
    const endBoundary = format(addDays(parseISO(clampedEnd), 1), 'yyyy-MM-dd')

    return {
      shapes: [
        {
          type: 'rect',
          xref: 'x',
          yref: 'paper',
          x0: clampedStart,
          x1: endBoundary,
          y0: 0,
          y1: 1,
          fillcolor: theme.colors.secondary,
          opacity: 0.12,
          line: {
            width: 0,
          },
          layer: 'below',
        },
        {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: clampedStart,
          x1: clampedStart,
          y0: 0,
          y1: 1,
          line: {
            color: theme.colors.secondary,
            dash: 'dot',
            width: 1,
          },
        },
        {
          type: 'line',
          xref: 'x',
          yref: 'paper',
          x0: endBoundary,
          x1: endBoundary,
          y0: 0,
          y1: 1,
          line: {
            color: theme.colors.secondary,
            dash: 'dot',
            width: 1,
          },
        },
      ],
    }
  }, [normalizedDates, selectedRangeEnd, selectedRangeStart, theme.colors.secondary])

  if (!isLoading && validValues.length === 0) {
    return null
  }

  if (validValues.length > 0 && validValues.every(value => value === 0)) {
    return (
      <Wrapper>
        <p
          style={{
            margin: '0',
            alignSelf: 'center',
            width: '100%',
            textAlign: 'center',
          }}
        >
          Sem dados para este preview.
        </p>
      </Wrapper>
    )
  }

  if (isLoading && validValues.length === 0) {
    return <Loader />
  }

  return (
    <Wrapper>
      <PlotFrame>
        <Plot
          data={data}
          layout={layout}
          layoutPatch={selectionLayoutPatch}
          containerHeight='100%'
          enableFullscreen
        />
      </PlotFrame>
      {isLoading ? (
        <LoadingOverlay>
          <Loader />
        </LoadingOverlay>
      ) : null}
    </Wrapper>
  )
}

function normalizeLegacyDate(date: string) {
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
    return format(parse(date, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd')
  }

  return date.slice(0, 10)
}

function ModernTimeSeriesPreview({
  series = [],
  bands,
  disableTooltip = false,
  disableYAxis = false,
  disablePoints = false,
  padding = 0,
}: Required<Pick<ModernProps, 'series'>> & Omit<ModernProps, 'series'>) {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const { dimensions, setObservedElement } = useObservedDimensions<HTMLDivElement>()
  const [tooltipData, setTooltipData] = useState<ModernTooltipData | null>(null)
  const [minValue, setMinValue] = useState<MinMaxPoint | null>(null)
  const [maxValue, setMaxValue] = useState<MinMaxPoint | null>(null)
  const theme = useTheme()
  const bottomPadding = 1
  const handleWrapperRef = useCallback(
    (element: HTMLDivElement | null) => {
      wrapperRef.current = element
      setObservedElement(element)
    },
    [setObservedElement],
  )

  useEffect(() => {
    const seriesValues = series.flatMap(currentSeries =>
      currentSeries.values.filter(value => value !== null),
    ) as number[]
    const bandValues: number[] = []

    if (bands) {
      if (typeof bands.upper === 'number' && Number.isFinite(bands.upper)) bandValues.push(bands.upper)
      if (typeof bands.lower === 'number' && Number.isFinite(bands.lower)) bandValues.push(bands.lower)
      if (typeof bands.median === 'number' && Number.isFinite(bands.median)) bandValues.push(bands.median)
      if (bands.shaded) {
        if (typeof bands.shaded.from === 'number' && Number.isFinite(bands.shaded.from)) {
          bandValues.push(bands.shaded.from)
        }
        if (typeof bands.shaded.to === 'number' && Number.isFinite(bands.shaded.to)) {
          bandValues.push(bands.shaded.to)
        }
      }
    }

    const validValues = [...seriesValues, ...bandValues]
    const computedMax = Math.floor(Math.max(...validValues))
    const computedMin = Math.floor(Math.min(...validValues))

    if (computedMax === -Infinity || computedMin === Infinity) {
      return
    }

    setMaxValue({ value: computedMax, index: -1 })
    setMinValue({
      value: computedMin === computedMax ? 0 : computedMin,
      index: -1,
    })
  }, [series, bands])

  const calculateY = useCallback(
    (value: number | null) => {
      if (value === null || minValue === null || maxValue === null) {
        return null
      }
      const adjustedHeight = Math.max(0, dimensions.height - bottomPadding)
      const range = maxValue.value - minValue.value
      if (range === 0) {
        return adjustedHeight / 2
      }

      return adjustedHeight - ((Math.floor(value) - minValue.value) / range) * adjustedHeight
    },
    [dimensions.height, bottomPadding, minValue, maxValue],
  )

  const globalLength = useMemo(() => {
    const lengths = series.map(currentSeries => currentSeries.values.length)
    if (lengths.length === 0) return 0
    return Math.max(...lengths)
  }, [series])

  const { pointsPerSeries, paths, areaPathsPerSeries } = useMemo(() => {
    const adjustedWidth = dimensions.width - padding * 2
    const pointWidth = globalLength > 1 ? adjustedWidth / (globalLength - 1) : 0
    const baseY = Math.max(0, dimensions.height - bottomPadding)

    const computedPoints = series.map(currentSeries =>
      currentSeries.values.map((value, index) => {
        const y = calculateY(value)
        if (y === null) {
          return null
        }
        const x = globalLength === 1 ? dimensions.width / 2 : index * pointWidth + padding
        return { x, y }
      }),
    )

    const computedPaths = computedPoints.map(points => {
      let d = ''
      for (let index = 0; index < points.length; index++) {
        const point = points[index]
        if (!point) continue
        if (d === '' || index === 0 || points[index - 1] === null) {
          d += `M ${point.x} ${point.y}`
        } else {
          d += ` L ${point.x} ${point.y}`
        }
      }
      return d
    })

    const computedAreaPathsPerSeries = computedPoints.map(points => {
      const pathsForSeries: string[] = []
      let currentSegment: Array<{ x: number; y: number }> = []

      for (let index = 0; index < points.length; index++) {
        const point = points[index]
        if (point) {
          currentSegment.push(point)
        } else if (currentSegment.length >= 2) {
          let d = `M ${currentSegment[0].x} ${currentSegment[0].y}`
          for (let segmentIndex = 1; segmentIndex < currentSegment.length; segmentIndex++) {
            d += ` L ${currentSegment[segmentIndex].x} ${currentSegment[segmentIndex].y}`
          }
          const lastPoint = currentSegment[currentSegment.length - 1]
          const firstPoint = currentSegment[0]
          d += ` L ${lastPoint.x} ${baseY} L ${firstPoint.x} ${baseY} Z`
          pathsForSeries.push(d)
          currentSegment = []
        } else {
          currentSegment = []
        }
      }

      if (currentSegment.length >= 2) {
        let d = `M ${currentSegment[0].x} ${currentSegment[0].y}`
        for (let segmentIndex = 1; segmentIndex < currentSegment.length; segmentIndex++) {
          d += ` L ${currentSegment[segmentIndex].x} ${currentSegment[segmentIndex].y}`
        }
        const lastPoint = currentSegment[currentSegment.length - 1]
        const firstPoint = currentSegment[0]
        d += ` L ${lastPoint.x} ${baseY} L ${firstPoint.x} ${baseY} Z`
        pathsForSeries.push(d)
      }

      return pathsForSeries
    })

    return {
      pointsPerSeries: computedPoints,
      paths: computedPaths,
      areaPathsPerSeries: computedAreaPathsPerSeries,
    }
  }, [calculateY, series, dimensions.width, dimensions.height, padding, bottomPadding, globalLength])

  const handleMouseEnter = (seriesIndex: number, pointIndex: number) => {
    const value = series[seriesIndex]?.values[pointIndex]
    setTooltipData({
      seriesIndex,
      pointIndex,
      value: value !== null && value !== undefined ? formatBR(value) : 'Sem dados',
    })
  }

  const handleMouseLeave = () => setTooltipData(null)

  const tooltipPos = useMemo(() => {
    if (!tooltipData || !wrapperRef.current) return null
    const rect = wrapperRef.current.getBoundingClientRect()
    const point = pointsPerSeries[tooltipData.seriesIndex]?.[tooltipData.pointIndex]
    if (!point) return null
    const tooltipWidth = 150
    const tooltipHeight = 60
    let left = rect.left + point.x - tooltipWidth / 2
    let top = rect.top + point.y - tooltipHeight - 10
    left = Math.max(8, Math.min(left, window.innerWidth - tooltipWidth - 8))
    top = Math.max(8, Math.min(top, window.innerHeight - tooltipHeight - 8))
    return { left, top }
  }, [tooltipData, pointsPerSeries])

  const allNonNullValues = [
    ...((series.flatMap(currentSeries => currentSeries.values.filter(value => value !== null)) as number[]) ?? []),
    ...(bands
      ? ([bands.upper, bands.lower, bands.median, bands.shaded?.from, bands.shaded?.to].filter(
          value => typeof value === 'number' && Number.isFinite(value as number),
        ) as number[])
      : []),
  ]

  if (allNonNullValues.length === 0 || allNonNullValues.every(value => value === 0)) {
    return (
      <Wrapper ref={handleWrapperRef}>
        <p
          style={{
            margin: '0',
            alignSelf: 'center',
            width: '100%',
            textAlign: 'center',
          }}
        >
          Sem dados para este preview.
        </p>
      </Wrapper>
    )
  }

  return (
    <Wrapper ref={handleWrapperRef}>
      <svg viewBox={`0 0 ${dimensions.width} ${dimensions.height}`} preserveAspectRatio='none'>
        {bands && minValue && maxValue && (
          <>
            {bands.shaded &&
              typeof bands.shaded.from === 'number' &&
              typeof bands.shaded.to === 'number' &&
              (() => {
                const yFrom = calculateY(bands.shaded!.from)
                const yTo = calculateY(bands.shaded!.to)
                if (yFrom === null || yTo === null) return null
                const y = Math.min(yFrom, yTo)
                const height = Math.abs(yTo - yFrom)
                const x = padding
                const width = Math.max(0, dimensions.width - padding * 2)
                return (
                  <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    fill={bands.shaded!.color}
                    opacity={typeof bands.shaded!.opacity === 'number' ? bands.shaded!.opacity : 0.2}
                  />
                )
              })()}
            {typeof bands.upper === 'number' &&
              (() => {
                const y = calculateY(bands.upper)
                if (y === null) return null
                return (
                  <line
                    x1={padding}
                    x2={Math.max(0, dimensions.width - padding)}
                    y1={y}
                    y2={y}
                    stroke={theme.colors.darkText}
                    strokeWidth={1}
                    strokeDasharray='4 4'
                    opacity={0.5}
                  />
                )
              })()}
            {typeof bands.lower === 'number' &&
              (() => {
                const y = calculateY(bands.lower)
                if (y === null) return null
                return (
                  <line
                    x1={padding}
                    x2={Math.max(0, dimensions.width - padding)}
                    y1={y}
                    y2={y}
                    stroke={theme.colors.darkText}
                    strokeWidth={1}
                    strokeDasharray='4 4'
                    opacity={0.5}
                  />
                )
              })()}
            {typeof bands.median === 'number' &&
              (() => {
                const y = calculateY(bands.median)
                if (y === null) return null
                return (
                  <line
                    x1={padding}
                    x2={Math.max(0, dimensions.width - padding)}
                    y1={y}
                    y2={y}
                    stroke={theme.colors.darkText}
                    strokeWidth={1}
                    opacity={0.7}
                  />
                )
              })()}
          </>
        )}
        {paths.length > 0 && (
          <>
            {areaPathsPerSeries.map((areaPaths, seriesIndex) =>
              areaPaths.map((d, areaIndex) => (
                <path
                  key={`area-${seriesIndex}-${areaIndex}`}
                  d={d}
                  fill={series[seriesIndex].fillColor ?? 'transparent'}
                  stroke='none'
                  opacity={0.2}
                />
              )),
            )}
            {paths.map((d, seriesIndex) => (
              <path
                key={`path-${seriesIndex}`}
                d={d}
                stroke={series[seriesIndex].color}
                fill='none'
                strokeWidth={3}
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            ))}
            {pointsPerSeries.map((points, seriesIndex) =>
              points.map(
                (point, pointIndex) =>
                  point !== null && (
                    <circle
                      key={`point-${seriesIndex}-${pointIndex}`}
                      cx={point.x}
                      cy={point.y}
                      r={3}
                      fill={disablePoints ? 'transparent' : series[seriesIndex].color}
                      onMouseEnter={() => handleMouseEnter(seriesIndex, pointIndex)}
                      onMouseLeave={handleMouseLeave}
                    />
                  ),
              ),
            )}
          </>
        )}
        {!disableYAxis && minValue && maxValue && (
          <>
            <line x1={-10} y1={2} x2={0} y2={2} stroke='#000' strokeWidth={1} />
            <line
              x1={-10}
              y1={dimensions.height - bottomPadding + 2}
              x2={0}
              y2={dimensions.height - bottomPadding + 2}
              stroke='#000'
              strokeWidth={1}
            />
            <line
              x1={-padding}
              y1={dimensions.height - bottomPadding + 1}
              x2={-padding}
              y2={3}
              stroke='#000'
              strokeWidth={1}
            />
            <text
              x={-14}
              y={dimensions.height - bottomPadding - 1}
              textAnchor='end'
              dominantBaseline='middle'
              fill={theme.colors.darkText}
              fontSize={12}
              overflow='visible'
            >
              {minValue.value}
            </text>
            <text
              x={-14}
              y={5}
              textAnchor='end'
              dominantBaseline='middle'
              fill={theme.colors.darkText}
              fontSize={12}
              overflow='visible'
            >
              {maxValue.value}
            </text>
          </>
        )}
      </svg>
      {!disableTooltip &&
        tooltipData &&
        tooltipPos &&
        createPortal(
          <ModernTooltipContainer style={{ left: tooltipPos.left, top: tooltipPos.top }}>
            <p>
              <strong>Valor:</strong> {tooltipData.value}
            </p>
          </ModernTooltipContainer>,
          document.body,
        )}
    </Wrapper>
  )
}

function TimeSeriesPreview(props: TimeSeriesPreviewProps) {
  if (Array.isArray(props.series)) {
    return (
      <ModernTimeSeriesPreview
        series={props.series}
        bands={props.bands}
        disableTooltip={props.disableTooltip}
        disableYAxis={props.disableYAxis}
        disablePoints={props.disablePoints}
        padding={props.padding}
      />
    )
  }

  return (
    <LegacyTimeSeriesPreview
      values={props.values}
      highlights={props.highlights}
      predictions={props.predictions}
      isLoading={props.isLoading}
      dates={props.dates}
      disableTooltip={props.disableTooltip}
      disableYAxis={props.disableYAxis}
      highlightColor={props.highlightColor}
      selectedRange={props.selectedRange}
    />
  )
}

export default memo(TimeSeriesPreview)
